import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useConfirm } from '../Common/ConfirmDialog';
import { useToast } from '../Common/ToastProvider';
import { Tooltip } from '../Common/ReusableUI';
import {
  AlertTriangle, Shield, Users, Phone, MapPin, Clock, Calendar,
  Search, Printer, CheckCircle, ChevronDown, ChevronRight,
  Flame, Droplets, Lock, Zap, UserCheck, PhoneCall, Radio,
  Plus, Activity, Megaphone, Heart, ClipboardList, Download, Save
} from 'lucide-react';

const PRIORITY_CONFIG = {
  1: { label: 'Priority 1 — Non-ambulant / Bed-bound', color: 'bg-red-600', text: 'text-white', badge: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500' },
  2: { label: 'Priority 2 — Wheelchair Dependent', color: 'bg-orange-500', text: 'text-white', badge: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500' },
  3: { label: 'Priority 3 — Walking Frame / Limited', color: 'bg-yellow-500', text: 'text-white', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500' },
  4: { label: 'Priority 4 — Independent / Ambulant', color: 'bg-green-600', text: 'text-white', badge: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-500' },
};

const DRILL_TYPES = ['Fire', 'Flood', 'Lockdown', 'Evacuation', 'Medical Emergency', 'Power Failure'];

const DRILL_TYPE_ICONS = {
  Fire: Flame,
  Flood: Droplets,
  Lockdown: Lock,
  Evacuation: Users,
  'Medical Emergency': Heart,
  'Power Failure': Zap,
};

const MOCK_RESIDENTS = [
  { id: 'R001', name: 'Margaret Thompson', room: 'B101', ward: 'Banksia', mobility: 'Bed-bound', priority: 1, assistance: '2-person hoist + nurse escort', location: 'Room B101', onLeave: false },
  { id: 'R002', name: 'Harold Mitchell', room: 'B103', ward: 'Banksia', mobility: 'Non-ambulant', priority: 1, assistance: '2-person hoist', location: 'Room B103', onLeave: false },
  { id: 'R003', name: 'Dorothy Evans', room: 'W201', ward: 'Waratah', mobility: 'Wheelchair dependent', priority: 2, assistance: '1-person wheelchair assist', location: 'Activity Room', onLeave: false },
  { id: 'R004', name: 'Arthur Williams', room: 'W205', ward: 'Waratah', mobility: 'Wheelchair dependent', priority: 2, assistance: '1-person wheelchair assist', location: 'Room W205', onLeave: false },
  { id: 'R005', name: 'Edna Clarke', room: 'G301', ward: 'Grevillea', mobility: 'Wheelchair dependent', priority: 2, assistance: '1-person wheelchair push', location: 'Dining Room', onLeave: false },
  { id: 'R006', name: 'Frank O\'Brien', room: 'B105', ward: 'Banksia', mobility: 'Walking frame', priority: 3, assistance: '1-person standby', location: 'Common Room', onLeave: false },
  { id: 'R007', name: 'Betty Johnson', room: 'W203', ward: 'Waratah', mobility: 'Walking frame', priority: 3, assistance: '1-person standby', location: 'Room W203', onLeave: false },
  { id: 'R008', name: 'George Patterson', room: 'G303', ward: 'Grevillea', mobility: 'Limited mobility', priority: 3, assistance: '1-person standby', location: 'Garden', onLeave: false },
  { id: 'R009', name: 'Jean Murray', room: 'G305', ward: 'Grevillea', mobility: 'Limited mobility', priority: 3, assistance: '1-person standby + walking aid', location: 'Room G305', onLeave: false },
  { id: 'R010', name: 'William Turner', room: 'B107', ward: 'Banksia', mobility: 'Independent', priority: 4, assistance: 'Independent', location: 'Library', onLeave: false },
  { id: 'R011', name: 'Shirley Campbell', room: 'W207', ward: 'Waratah', mobility: 'Independent', priority: 4, assistance: 'Independent', location: 'Room W207', onLeave: false },
  { id: 'R012', name: 'Robert Hughes', room: 'G307', ward: 'Grevillea', mobility: 'Independent', priority: 4, assistance: 'Independent', location: 'Café', onLeave: false },
  { id: 'R013', name: 'Nancy White', room: 'W209', ward: 'Waratah', mobility: 'Independent', priority: 4, assistance: 'Independent', location: 'Room W209', onLeave: false },
  { id: 'R014', name: 'Alan Foster', room: 'B109', ward: 'Banksia', mobility: 'Non-ambulant', priority: 1, assistance: '2-person stretcher', location: 'Room B109', onLeave: false },
  { id: 'R015', name: 'Patricia Kelly', room: 'G309', ward: 'Grevillea', mobility: 'Walking frame', priority: 3, assistance: '1-person standby', location: 'Physiotherapy', onLeave: true },
];

const MOCK_DRILLS = [
  { id: 'D001', date: '2026-03-15', type: 'Fire', participants: 42, duration: '18 min', outcomes: 'All residents evacuated within target time. Assembly point procedures followed correctly.', correctiveActions: 'Replace emergency signage on Level 2 corridor', completedBy: 'Sarah Chen (RN)', nextDue: '2026-06-15', status: 'completed' },
  { id: 'D002', date: '2026-02-28', type: 'Evacuation', participants: 38, duration: '22 min', outcomes: 'Full evacuation drill. Minor delays with wheelchair-dependent residents in Waratah wing.', correctiveActions: 'Additional wheelchair ramp training for night staff', completedBy: 'James Morton (CM)', nextDue: '2026-05-28', status: 'completed' },
  { id: 'D003', date: '2026-02-10', type: 'Lockdown', participants: 35, duration: '12 min', outcomes: 'Lockdown secured in all wings. Communication chain tested successfully.', correctiveActions: 'None required', completedBy: 'Sarah Chen (RN)', nextDue: '2026-05-10', status: 'completed' },
  { id: 'D004', date: '2026-01-20', type: 'Medical Emergency', participants: 15, duration: '8 min', outcomes: 'Code Blue simulation. Defibrillator deployed within 2 minutes. CPR protocols followed.', correctiveActions: 'Restock emergency trolley supplies', completedBy: 'Dr. Lisa Nguyen', nextDue: '2026-04-20', status: 'overdue' },
  { id: 'D005', date: '2025-12-05', type: 'Power Failure', participants: 28, duration: '25 min', outcomes: 'Backup generator activated in 30 seconds. Critical equipment maintained. Communication plan executed.', correctiveActions: 'Test emergency lighting in Grevillea wing monthly', completedBy: 'James Morton (CM)', nextDue: '2026-03-05', status: 'overdue' },
  { id: 'D006', date: '2025-11-18', type: 'Fire', participants: 40, duration: '16 min', outcomes: 'Night shift fire drill. Evacuated within acceptable timeframe. Fire warden roles clear.', correctiveActions: 'Update fire warden roster for December', completedBy: 'Maria Santos (RN)', nextDue: '2026-02-18', status: 'completed' },
  { id: 'D007', date: '2025-10-22', type: 'Flood', participants: 30, duration: '20 min', outcomes: 'Simulated water ingress in Banksia ground floor. Sandbag deployment practiced.', correctiveActions: 'Purchase additional sandbags, check sump pumps', completedBy: 'James Morton (CM)', nextDue: '2026-04-22', status: 'overdue' },
  { id: 'D008', date: '2025-09-14', type: 'Evacuation', participants: 44, duration: '19 min', outcomes: 'Annual full-facility evacuation. All staff participated. Muster point count accurate.', correctiveActions: 'Improve signage for visitor evacuation routes', completedBy: 'Sarah Chen (RN)', nextDue: '2026-03-14', status: 'completed' },
];

const CONTACT_TREE = {
  internal: [
    {
      id: 'C001', name: 'James Morton', role: 'Facility Manager', phone: '0412 345 678', secondary: '03 9876 5432', availability: 'available',
      reports: [
        {
          id: 'C002', name: 'Sarah Chen', role: 'Clinical Manager (DON)', phone: '0423 456 789', secondary: '03 9876 5433', availability: 'available',
          reports: [
            { id: 'C003', name: 'Maria Santos', role: 'Registered Nurse', phone: '0434 567 890', secondary: null, availability: 'available', reports: [] },
            { id: 'C004', name: 'Dr. Lisa Nguyen', role: 'Registered Nurse', phone: '0445 678 901', secondary: null, availability: 'on-shift', reports: [] },
            { id: 'C005', name: 'Tom Bradley', role: 'Registered Nurse', phone: '0456 789 012', secondary: null, availability: 'off-duty', reports: [] },
          ],
        },
        {
          id: 'C006', name: 'Karen White', role: 'Care Team Leader', phone: '0467 890 123', secondary: '03 9876 5434', availability: 'on-shift',
          reports: [
            { id: 'C007', name: 'David Kim', role: 'Care Worker', phone: '0478 901 234', secondary: null, availability: 'on-shift', reports: [] },
            { id: 'C008', name: 'Emily Brown', role: 'Care Worker', phone: '0489 012 345', secondary: null, availability: 'on-shift', reports: [] },
            { id: 'C009', name: 'Michael Lee', role: 'Care Worker', phone: '0490 123 456', secondary: null, availability: 'off-duty', reports: [] },
            { id: 'C010', name: 'Sophie Taylor', role: 'Care Worker', phone: '0401 234 567', secondary: null, availability: 'available', reports: [] },
          ],
        },
      ],
    },
  ],
  emergency: [
    { id: 'E001', name: 'Emergency Services', role: 'Triple Zero', phone: '000', secondary: null, availability: 'always' },
    { id: 'E002', name: 'Fire & Rescue NSW', role: 'Fire Brigade', phone: '000', secondary: '1300 729 579', availability: 'always' },
    { id: 'E003', name: 'Ambulance Victoria', role: 'Ambulance', phone: '000', secondary: '1800 008 008', availability: 'always' },
    { id: 'E004', name: 'Victoria Police', role: 'Police', phone: '000', secondary: '131 444', availability: 'always' },
    { id: 'E005', name: 'Poisons Information Centre', role: 'Poisons Info', phone: '13 11 26', secondary: null, availability: 'always' },
    { id: 'E006', name: 'SES – State Emergency', role: 'SES', phone: '132 500', secondary: null, availability: 'always' },
  ],
};

const PRIORITY_TOOLTIPS = {
  1: 'Non-ambulant / bed-bound — requires 2-person hoist or stretcher. Highest evacuation urgency.',
  2: 'Wheelchair-dependent — requires 1-person wheelchair assistance for evacuation.',
  3: 'Walking frame or limited mobility — requires standby supervision during evacuation.',
  4: 'Independent ambulant — can self-evacuate to assembly points with verbal direction.',
};

const AVAILABILITY_TOOLTIPS = {
  available: 'Available and not currently on shift — can be contacted immediately',
  'on-shift': 'Currently on shift at the facility — reachable on-site',
  'off-duty': 'Off duty — may have delayed response time',
  always: 'Emergency service — available 24/7',
};

const CA_STATUS_STYLES = {
  Open: 'bg-red-100 text-red-800 border-red-300',
  'In Progress': 'bg-amber-100 text-amber-800 border-amber-300',
  Closed: 'bg-green-100 text-green-800 border-green-300',
};

const Emergency = () => {
  const { state } = useAppContext();
  const confirm = useConfirm();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('evacuation');
  const [evacuationActive, setEvacuationActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [wardFilter, setWardFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [evacuatedResidents, setEvacuatedResidents] = useState(new Set());

  const [drillTypeFilter, setDrillTypeFilter] = useState('all');
  const [drillDateFrom, setDrillDateFrom] = useState('');
  const [drillDateTo, setDrillDateTo] = useState('');
  const [showNewDrillForm, setShowNewDrillForm] = useState(false);
  const [drills, setDrills] = useState(MOCK_DRILLS);
  const [newDrill, setNewDrill] = useState({ date: '', type: 'Fire', participants: '', duration: '', outcomes: '', correctiveActions: '', completedBy: '' });

  const [expandedContacts, setExpandedContacts] = useState(new Set(['C001', 'C002', 'C006']));
  const [smsTarget, setSmsTarget] = useState(null);
  const [smsSent, setSmsSent] = useState(new Set());

  // (a) Elapsed-time counter state
  const [evacuationStartTime, setEvacuationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // (c) Drill scheduling state
  const [scheduledDrills, setScheduledDrills] = useState([
    { id: 'SD001', type: 'Fire', date: '2026-06-15', coordinator: 'Sarah Chen (RN)', notes: 'Quarterly fire drill — all wards', status: 'scheduled' },
    { id: 'SD002', type: 'Evacuation', date: '2026-05-28', coordinator: 'James Morton (CM)', notes: 'Focus on wheelchair-dependent residents', status: 'scheduled' },
  ]);
  const [showScheduleDrillForm, setShowScheduleDrillForm] = useState(false);
  const [newScheduledDrill, setNewScheduledDrill] = useState({ type: 'Fire', date: '', coordinator: '', notes: '' });

  // (e) Corrective action tracking state
  const [drillCorrectiveActions, setDrillCorrectiveActions] = useState(() => {
    const map = {};
    MOCK_DRILLS.forEach(d => {
      if (d.correctiveActions && d.correctiveActions !== 'None required') {
        map[d.id] = [{ id: `CA-${d.id}-1`, description: d.correctiveActions, status: 'Open', assignedTo: d.completedBy, dueDate: d.nextDue }];
      }
    });
    return map;
  });
  const [expandedDrillId, setExpandedDrillId] = useState(null);
  const [newCorrectiveAction, setNewCorrectiveAction] = useState({ description: '', assignedTo: '', dueDate: '' });

  const facilityResidents = useMemo(
    () => MOCK_RESIDENTS.filter((r) => !r.onLeave).sort((a, b) => a.priority - b.priority),
    [],
  );

  const filteredResidents = useMemo(() => {
    return facilityResidents.filter((r) => {
      const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.room.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesWard = wardFilter === 'all' || r.ward === wardFilter;
      const matchesPriority = priorityFilter === 'all' || r.priority === Number(priorityFilter);
      return matchesSearch && matchesWard && matchesPriority;
    });
  }, [facilityResidents, searchQuery, wardFilter, priorityFilter]);

  const priorityCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    facilityResidents.forEach((r) => { counts[r.priority]++; });
    return counts;
  }, [facilityResidents]);

  const filteredDrills = useMemo(() => {
    return drills.filter((d) => {
      const matchesType = drillTypeFilter === 'all' || d.type === drillTypeFilter;
      const matchesFrom = !drillDateFrom || d.date >= drillDateFrom;
      const matchesTo = !drillDateTo || d.date <= drillDateTo;
      return matchesType && matchesFrom && matchesTo;
    });
  }, [drills, drillTypeFilter, drillDateFrom, drillDateTo]);

  // (a) Elapsed-time interval
  useEffect(() => {
    if (!evacuationActive || !evacuationStartTime) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - evacuationStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [evacuationActive, evacuationStartTime]);

  const formatElapsed = (totalSeconds) => {
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const s = String(totalSeconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  // (f) Toast on activate/deactivate
  const handleActivateEvacuation = async () => {
    if (evacuationActive) {
      if (await confirm({ title: 'Deactivate Evacuation', message: 'Deactivate evacuation mode? Ensure all residents are accounted for.', confirmLabel: 'Deactivate', variant: 'warning' })) {
        setEvacuationActive(false);
        setEvacuatedResidents(new Set());
        setEvacuationStartTime(null);
        setElapsedTime(0);
        showToast('Evacuation mode deactivated — all clear', 'warning');
        console.log('[Emergency] Evacuation mode deactivated at', new Date().toISOString());
      }
    } else {
      if (await confirm({ title: 'ACTIVATE EVACUATION', message: 'This will alert all staff and begin the evacuation protocol.', confirmLabel: 'Activate Evacuation', variant: 'danger' })) {
        setEvacuationActive(true);
        setEvacuationStartTime(Date.now());
        showToast('EVACUATION ACTIVATED — follow protocol immediately', 'error', 8000);
        console.log('[Emergency] EVACUATION ACTIVATED at', new Date().toISOString());
      }
    }
  };

  const toggleEvacuated = (residentId) => {
    setEvacuatedResidents((prev) => {
      const next = new Set(prev);
      if (next.has(residentId)) {
        next.delete(residentId);
      } else {
        next.add(residentId);
      }
      console.log(`[Emergency] Resident ${residentId} marked ${next.has(residentId) ? 'EVACUATED' : 'NOT evacuated'}`);
      return next;
    });
  };

  const handleAddDrill = (e) => {
    e.preventDefault();
    const drill = {
      id: `D${String(drills.length + 1).padStart(3, '0')}`,
      ...newDrill,
      participants: Number(newDrill.participants),
      nextDue: (() => {
        const d = new Date(newDrill.date);
        d.setMonth(d.getMonth() + 3);
        return d.toISOString().split('T')[0];
      })(),
      status: 'completed',
    };
    setDrills((prev) => [drill, ...prev]);
    setNewDrill({ date: '', type: 'Fire', participants: '', duration: '', outcomes: '', correctiveActions: '', completedBy: '' });
    setShowNewDrillForm(false);
    showToast('Drill recorded successfully', 'success');
    console.log('[Emergency] New drill recorded:', drill.id, drill.type);
  };

  const toggleContactExpanded = (id) => {
    setExpandedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // (b) SMS message preview before sending
  const handleSendSMS = async (contact) => {
    const smsBody = 'EMERGENCY: Evacuation in progress at MedFast Aged Care. Report to your assembly point immediately. Contact Facility Manager for instructions.';
    const confirmed = await confirm({
      title: 'Send Emergency SMS',
      message: `To: ${contact.name} (${contact.phone})\n\nPreview:\n"${smsBody}"`,
      confirmLabel: 'Send SMS',
      variant: 'danger',
    });
    if (!confirmed) return;
    setSmsTarget(contact);
    setTimeout(() => {
      setSmsSent((prev) => new Set(prev).add(contact.id));
      setSmsTarget(null);
      showToast(`SMS sent to ${contact.name}`, 'success');
      console.log(`[Emergency] SMS sent to ${contact.name} (${contact.phone})`);
    }, 1200);
  };

  // (b) Mass SMS preview
  const handleMassSMS = async () => {
    const allStaff = [];
    const collect = (node) => { allStaff.push(node); node.reports?.forEach(collect); };
    CONTACT_TREE.internal.forEach(collect);
    const confirmed = await confirm({
      title: 'Mass Emergency SMS',
      message: `Send emergency SMS to all ${allStaff.length} staff members?\n\nPreview:\n"EMERGENCY: Evacuation in progress at MedFast Aged Care. Report to your assembly point immediately."`,
      confirmLabel: `Send to ${allStaff.length} Staff`,
      variant: 'danger',
    });
    if (!confirmed) return;
    allStaff.forEach((c) => {
      setSmsSent((prev) => new Set(prev).add(c.id));
    });
    showToast(`Mass SMS sent to ${allStaff.length} staff members`, 'success');
    console.log('[Emergency] Mass SMS alert sent to', allStaff.length, 'staff');
  };

  const handlePrint = () => {
    console.log('[Emergency] Print evacuation list triggered');
    window.print();
  };

  // (d) Export drill records as CSV
  const exportDrillsCSV = () => {
    const headers = ['ID', 'Date', 'Type', 'Participants', 'Duration', 'Completed By', 'Outcomes', 'Corrective Actions', 'Next Due', 'Status'];
    const rows = filteredDrills.map(d => [
      d.id, d.date, d.type, d.participants, d.duration,
      `"${d.completedBy}"`, `"${(d.outcomes || '').replace(/"/g, '""')}"`,
      `"${(d.correctiveActions || '').replace(/"/g, '""')}"`, d.nextDue, d.status,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drill_records_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Drill records exported to CSV', 'success');
    console.log('[Emergency] Drill records exported to CSV');
  };

  // (c) Schedule a new drill
  const handleAddScheduledDrill = (e) => {
    e.preventDefault();
    const drill = {
      id: `SD${String(scheduledDrills.length + 3).padStart(3, '0')}`,
      ...newScheduledDrill,
      status: 'scheduled',
    };
    setScheduledDrills(prev => [...prev, drill]);
    setNewScheduledDrill({ type: 'Fire', date: '', coordinator: '', notes: '' });
    setShowScheduleDrillForm(false);
    showToast(`Drill scheduled for ${drill.date}`, 'success');
    console.log('[Emergency] Drill scheduled:', drill.id, drill.type, drill.date);
  };

  // (e) Corrective action management
  const addCorrectiveAction = (drillId) => {
    if (!newCorrectiveAction.description.trim()) return;
    const ca = {
      id: `CA-${drillId}-${Date.now()}`,
      description: newCorrectiveAction.description,
      status: 'Open',
      assignedTo: newCorrectiveAction.assignedTo || 'Unassigned',
      dueDate: newCorrectiveAction.dueDate || '',
    };
    setDrillCorrectiveActions(prev => ({
      ...prev,
      [drillId]: [...(prev[drillId] || []), ca],
    }));
    setNewCorrectiveAction({ description: '', assignedTo: '', dueDate: '' });
    showToast('Corrective action added', 'success');
  };

  const updateCorrectiveActionStatus = (drillId, caId, newStatus) => {
    setDrillCorrectiveActions(prev => ({
      ...prev,
      [drillId]: (prev[drillId] || []).map(ca =>
        ca.id === caId ? { ...ca, status: newStatus } : ca
      ),
    }));
  };

  const availabilityBadge = (status) => {
    const map = {
      available: 'bg-green-100 text-green-800',
      'on-shift': 'bg-blue-100 text-blue-800',
      'off-duty': 'bg-gray-100 text-gray-600',
      always: 'bg-emerald-100 text-emerald-800',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  // (f) Tooltips on availability badges
  const renderContactNode = (contact, depth = 0) => {
    const hasReports = contact.reports && contact.reports.length > 0;
    const isExpanded = expandedContacts.has(contact.id);
    const isSending = smsTarget?.id === contact.id;
    const wasSent = smsSent.has(contact.id);

    return (
      <div key={contact.id} style={{ marginLeft: depth * 24 }}>
        <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${depth === 0 ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'} hover:shadow-sm`}>
          {hasReports ? (
            <button onClick={() => toggleContactExpanded(contact.id)} className="p-1 rounded hover:bg-gray-200 transition-colors">
              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            </button>
          ) : (
            <span className="w-6" />
          )}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${depth === 0 ? 'bg-purple-600 text-white' : depth === 1 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
            <UserCheck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900 truncate">{contact.name}</span>
              <Tooltip content={AVAILABILITY_TOOLTIPS[contact.availability] || contact.availability} position="bottom">
                <span className={`px-2 py-0.5 text-xs rounded-full cursor-help ${availabilityBadge(contact.availability)}`}>{contact.availability.replace('-', ' ')}</span>
              </Tooltip>
            </div>
            <p className="text-xs text-gray-500">{contact.role}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right mr-2">
              <p className="text-sm font-medium text-gray-800">{contact.phone}</p>
              {contact.secondary && <p className="text-xs text-gray-400">{contact.secondary}</p>}
            </div>
            {evacuationActive && (
              <button
                onClick={() => handleSendSMS(contact)}
                disabled={isSending || wasSent}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${wasSent ? 'bg-green-100 text-green-700 cursor-default' : isSending ? 'bg-yellow-100 text-yellow-700 animate-pulse' : 'bg-red-600 text-white hover:bg-red-700'}`}
              >
                {wasSent ? <><CheckCircle className="w-3 h-3" /> Sent</> : isSending ? <><Radio className="w-3 h-3 animate-spin" /> Sending…</> : <><PhoneCall className="w-3 h-3" /> SMS</>}
              </button>
            )}
          </div>
        </div>
        {hasReports && isExpanded && (
          <div className="mt-1 space-y-1">
            {contact.reports.map((child) => renderContactNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'evacuation', label: 'Evacuation List', icon: Shield },
    { key: 'drills', label: 'Drill Records', icon: ClipboardList },
    { key: 'contacts', label: 'Contact Tree', icon: Phone },
  ];

  return (
    <div className="space-y-6">
      {/* (a) Evacuation banner with elapsed-time counter */}
      {evacuationActive && (
        <div className="bg-red-600 text-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center justify-center gap-3 py-2.5 bg-red-700/60">
            <Clock className="w-5 h-5 animate-pulse" />
            <span className="text-3xl font-mono font-black tracking-widest animate-pulse">{formatElapsed(elapsedTime)}</span>
            <span className="text-red-200 text-sm font-medium">elapsed</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold tracking-wide">⚠ EVACUATION IN PROGRESS</h2>
                <p className="text-red-100 text-sm">All staff follow evacuation protocol — account for every resident</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{evacuatedResidents.size} / {facilityResidents.length} evacuated</p>
                <div className="w-40 bg-red-800 rounded-full h-2 mt-1">
                  <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${(evacuatedResidents.size / facilityResidents.length) * 100}%` }} />
                </div>
              </div>
              <button onClick={handleActivateEvacuation} className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-red-50 transition-colors">
                STAND DOWN
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="w-8 h-8" /> Evacuation & Emergency
            </h1>
            <p className="text-red-100 text-base mt-2">Emergency preparedness, drill management, and contact coordination</p>
          </div>
          {!evacuationActive && (
            <button onClick={handleActivateEvacuation} className="bg-white text-red-600 px-6 py-3 rounded-xl font-bold text-lg hover:bg-red-50 transition-all shadow-lg flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" /> ACTIVATE EVACUATION
            </button>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === key ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'evacuation' && (
        <div className="space-y-6">
          {/* (f) Priority cards with tooltips */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((p) => (
              <Tooltip key={p} content={PRIORITY_TOOLTIPS[p]} position="bottom">
                <div className={`rounded-xl p-4 border ${PRIORITY_CONFIG[p].badge} flex items-center gap-3 cursor-help`}>
                  <div className={`w-10 h-10 ${PRIORITY_CONFIG[p].color} rounded-full flex items-center justify-center`}>
                    <span className={`text-lg font-bold ${PRIORITY_CONFIG[p].text}`}>{priorityCounts[p]}</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium leading-tight">Priority {p}</p>
                    <p className="text-[11px] opacity-75">{PRIORITY_CONFIG[p].label.split('—')[1]?.trim()}</p>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search resident or room…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <select value={wardFilter} onChange={(e) => setWardFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
              <option value="all">All Wards</option>
              <option value="Banksia">Banksia</option>
              <option value="Waratah">Waratah</option>
              <option value="Grevillea">Grevillea</option>
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
              <option value="all">All Priorities</option>
              <option value="1">Priority 1</option>
              <option value="2">Priority 2</option>
              <option value="3">Priority 3</option>
              <option value="4">Priority 4</option>
            </select>
            <button onClick={handlePrint} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium">
              <Printer className="w-4 h-4" /> Print List
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {evacuationActive && <th className="px-4 py-3 text-left font-semibold text-gray-700 w-12">Safe</th>}
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Resident</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Room</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Ward</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Mobility</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Priority</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Assistance Required</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResidents.map((r) => {
                    const cfg = PRIORITY_CONFIG[r.priority];
                    const isEvacuated = evacuatedResidents.has(r.id);
                    return (
                      <tr key={r.id} className={`border-b border-gray-100 transition-colors ${isEvacuated ? 'bg-green-50' : evacuationActive ? 'hover:bg-red-50' : 'hover:bg-gray-50'}`}>
                        {evacuationActive && (
                          <td className="px-4 py-3">
                            <button onClick={() => toggleEvacuated(r.id)} className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${isEvacuated ? 'bg-green-600 border-green-600 text-white' : 'border-gray-300 hover:border-red-400'}`}>
                              {isEvacuated && <CheckCircle className="w-4 h-4" />}
                            </button>
                          </td>
                        )}
                        <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                        <td className="px-4 py-3 text-gray-600">{r.room}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{r.ward}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{r.mobility}</td>
                        <td className="px-4 py-3">
                          <Tooltip content={PRIORITY_TOOLTIPS[r.priority]} position="left">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border cursor-help ${cfg.badge}`}>
                              <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                              P{r.priority}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{r.assistance}</td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-gray-600 text-xs"><MapPin className="w-3 h-3" /> {r.location}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredResidents.length === 0 && (
                    <tr><td colSpan={evacuationActive ? 8 : 7} className="px-4 py-12 text-center text-gray-400">No residents match current filters</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <span>{filteredResidents.length} resident{filteredResidents.length !== 1 && 's'} shown — {MOCK_RESIDENTS.filter((r) => r.onLeave).length} on leave (excluded)</span>
              {evacuationActive && <span className="font-medium text-green-700">{evacuatedResidents.size} marked safe</span>}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'drills' && (
        <div className="space-y-6">
          {/* Filters + action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Drill Type</label>
              <select value={drillTypeFilter} onChange={(e) => setDrillTypeFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <option value="all">All Types</option>
                {DRILL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={drillDateFrom} onChange={(e) => setDrillDateFrom(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={drillDateTo} onChange={(e) => setDrillDateTo(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div className="flex-1" />
            {/* (d) Export CSV */}
            <button onClick={exportDrillsCSV} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium text-sm">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            {/* (c) Schedule Drill */}
            <button onClick={() => setShowScheduleDrillForm(!showScheduleDrillForm)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 font-medium text-sm">
              <Calendar className="w-4 h-4" /> {showScheduleDrillForm ? 'Cancel' : 'Schedule Drill'}
            </button>
            <button onClick={() => setShowNewDrillForm(!showNewDrillForm)} className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium">
              <Plus className="w-4 h-4" /> {showNewDrillForm ? 'Cancel' : 'Record Drill'}
            </button>
          </div>

          {/* Record Drill form (existing) */}
          {showNewDrillForm && (
            <form onSubmit={handleAddDrill} className="bg-purple-50 border border-purple-200 rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> New Emergency Drill Record</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" required value={newDrill.date} onChange={(e) => setNewDrill({ ...newDrill, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                  <select required value={newDrill.type} onChange={(e) => setNewDrill({ ...newDrill, type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    {DRILL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Participants *</label>
                  <input type="number" required min="1" value={newDrill.participants} onChange={(e) => setNewDrill({ ...newDrill, participants: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Duration *</label>
                  <input type="text" required placeholder="e.g. 18 min" value={newDrill.duration} onChange={(e) => setNewDrill({ ...newDrill, duration: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Completed By *</label>
                  <input type="text" required placeholder="Name and role" value={newDrill.completedBy} onChange={(e) => setNewDrill({ ...newDrill, completedBy: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Outcomes / Notes *</label>
                <textarea required rows={2} value={newDrill.outcomes} onChange={(e) => setNewDrill({ ...newDrill, outcomes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Corrective Actions Required</label>
                <textarea rows={2} value={newDrill.correctiveActions} onChange={(e) => setNewDrill({ ...newDrill, correctiveActions: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex justify-end">
                <button type="submit" className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Save Drill Record
                </button>
              </div>
            </form>
          )}

          {/* (c) Schedule Drill form */}
          {showScheduleDrillForm && (
            <form onSubmit={handleAddScheduledDrill} className="bg-indigo-50 border border-indigo-200 rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold text-indigo-800 flex items-center gap-2"><Calendar className="w-5 h-5" /> Schedule Upcoming Drill</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Drill Type *</label>
                  <select required value={newScheduledDrill.type} onChange={(e) => setNewScheduledDrill({ ...newScheduledDrill, type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                    {DRILL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Scheduled Date *</label>
                  <input type="date" required value={newScheduledDrill.date} onChange={(e) => setNewScheduledDrill({ ...newScheduledDrill, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Assigned Coordinator *</label>
                  <input type="text" required placeholder="Name and role" value={newScheduledDrill.coordinator} onChange={(e) => setNewScheduledDrill({ ...newScheduledDrill, coordinator: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <input type="text" placeholder="Optional notes" value={newScheduledDrill.notes} onChange={(e) => setNewScheduledDrill({ ...newScheduledDrill, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="flex justify-end">
                <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Schedule Drill
                </button>
              </div>
            </form>
          )}

          {/* (c) Upcoming scheduled drills */}
          {scheduledDrills.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-3"><Calendar className="w-5 h-5" /> Upcoming Scheduled Drills</h3>
              <div className="space-y-2">
                {scheduledDrills.sort((a, b) => a.date.localeCompare(b.date)).map(sd => {
                  const DrillIcon = DRILL_TYPE_ICONS[sd.type] || AlertTriangle;
                  return (
                    <div key={sd.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-blue-100">
                      <DrillIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-gray-900">{sd.type} Drill</span>
                        <span className="text-xs text-gray-500 ml-2">{sd.date}</span>
                      </div>
                      <div className="text-xs text-gray-500 hidden sm:block">{sd.coordinator}</div>
                      {sd.notes && <div className="text-xs text-gray-400 max-w-[200px] truncate hidden md:block">{sd.notes}</div>}
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Scheduled</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Drill history with (e) structured corrective actions */}
          <div className="space-y-4">
            {filteredDrills.map((drill) => {
              const DrillIcon = DRILL_TYPE_ICONS[drill.type] || AlertTriangle;
              const actions = drillCorrectiveActions[drill.id] || [];
              const isExpanded = expandedDrillId === drill.id;
              return (
                <div key={drill.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${drill.type === 'Fire' ? 'bg-red-100 text-red-600' : drill.type === 'Flood' ? 'bg-blue-100 text-blue-600' : drill.type === 'Lockdown' ? 'bg-yellow-100 text-yellow-700' : drill.type === 'Medical Emergency' ? 'bg-pink-100 text-pink-600' : drill.type === 'Power Failure' ? 'bg-orange-100 text-orange-600' : 'bg-purple-100 text-purple-600'}`}>
                      <DrillIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h3 className="font-semibold text-gray-900">{drill.type} Drill</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${drill.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                          {drill.status === 'overdue' ? 'Next drill overdue' : 'Completed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {drill.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {drill.duration}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {drill.participants} participants</span>
                        <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> {drill.completedBy}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{drill.outcomes}</p>
                      <div className="mt-2 text-xs text-gray-400">Next drill due: <span className={drill.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-600'}>{drill.nextDue}</span></div>

                      {/* (e) Corrective actions toggle */}
                      <button onClick={() => setExpandedDrillId(isExpanded ? null : drill.id)} className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1">
                        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        Corrective Actions ({actions.length})
                      </button>

                      {/* (e) Expanded corrective actions panel */}
                      {isExpanded && (
                        <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
                          {actions.map(ca => (
                            <div key={ca.id} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3 text-sm">
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-800">{ca.description}</p>
                                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                                  <span>Assigned: <strong>{ca.assignedTo}</strong></span>
                                  {ca.dueDate && <span>Due: {ca.dueDate}</span>}
                                </div>
                              </div>
                              <select
                                value={ca.status}
                                onChange={(e) => updateCorrectiveActionStatus(drill.id, ca.id, e.target.value)}
                                className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer ${CA_STATUS_STYLES[ca.status] || 'bg-gray-100 text-gray-800 border-gray-300'}`}
                              >
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Closed">Closed</option>
                              </select>
                            </div>
                          ))}
                          {actions.length === 0 && (
                            <p className="text-xs text-gray-400 italic">No corrective actions recorded</p>
                          )}
                          <div className="flex gap-2 mt-2">
                            <input type="text" placeholder="Description" value={newCorrectiveAction.description} onChange={(e) => setNewCorrectiveAction({ ...newCorrectiveAction, description: e.target.value })} className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500" />
                            <input type="text" placeholder="Assigned to" value={newCorrectiveAction.assignedTo} onChange={(e) => setNewCorrectiveAction({ ...newCorrectiveAction, assignedTo: e.target.value })} className="w-32 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500" />
                            <input type="date" value={newCorrectiveAction.dueDate} onChange={(e) => setNewCorrectiveAction({ ...newCorrectiveAction, dueDate: e.target.value })} className="w-36 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500" />
                            <button onClick={() => addCorrectiveAction(drill.id)} disabled={!newCorrectiveAction.description.trim()} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredDrills.length === 0 && (
              <div className="text-center py-12 text-gray-400">No drill records match the selected filters</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Users className="w-5 h-5 text-purple-600" /> Staff Contact Hierarchy</h3>
              <div className="space-y-2">
                {CONTACT_TREE.internal.map((c) => renderContactNode(c, 0))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Phone className="w-5 h-5 text-red-600" /> Emergency Services</h3>
              <div className="space-y-2">
                {CONTACT_TREE.emergency.map((svc) => (
                  <div key={svc.id} className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                      {svc.role === 'Fire Brigade' ? <Flame className="w-5 h-5" /> : svc.role === 'Ambulance' ? <Activity className="w-5 h-5" /> : svc.role === 'Police' ? <Shield className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900">{svc.name}</p>
                      <p className="text-xs text-gray-500">{svc.role}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-red-700">{svc.phone}</p>
                      {svc.secondary && <p className="text-xs text-gray-500">{svc.secondary}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* (b) Mass SMS with preview */}
              {evacuationActive && (
                <div className="bg-red-100 border border-red-300 rounded-xl p-4">
                  <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3"><Megaphone className="w-5 h-5" /> Mass Alert</h4>
                  <button
                    onClick={handleMassSMS}
                    className="w-full py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Radio className="w-4 h-4" /> Send Emergency SMS to All Staff
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emergency;
